"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeCommercialCounts = normalizeCommercialCounts;
exports.normalizeCommercialBillingCycle = normalizeCommercialBillingCycle;
exports.getBillingCycleMonths = getBillingCycleMonths;
exports.advanceCommercialCycleStart = advanceCommercialCycleStart;
exports.computeInitialNextRenewalAt = computeInitialNextRenewalAt;
exports.isUnlimitedPlanLimit = isUnlimitedPlanLimit;
exports.planSupportsCounts = planSupportsCounts;
exports.planSupportsBillingCycle = planSupportsBillingCycle;
exports.readBillingCycleAdjustment = readBillingCycleAdjustment;
exports.selectSmallestEligiblePlan = selectSmallestEligiblePlan;
exports.estimateCommercialCycleAmount = estimateCommercialCycleAmount;
exports.resolveCommercialSelection = resolveCommercialSelection;
const tenant_access_1 = require("./tenant-access");
const DEFAULT_BILLING_CYCLE = "monthly";
const TIER_PRIORITY = {
    basic: 10,
    safety: 20,
    ai: 30,
    enterprise: 40,
    custom: 50,
};
function roundMoney(value) {
    return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
}
function normalizeCommercialCounts(input) {
    const toInt = (value, fallback) => Number.isFinite(value) ? Math.max(0, Math.floor(value)) : fallback;
    return {
        seatCount: Math.max(1, toInt(input.seatCount, 1)),
        vehicleCount: toInt(input.vehicleCount, 0),
        deviceCount: toInt(input.deviceCount, 0),
    };
}
function normalizeCommercialBillingCycle(value) {
    const raw = String(value ?? "").trim().toLowerCase();
    if (raw === "quarterly")
        return "quarterly";
    if (raw === "biannual")
        return "biannual";
    if (raw === "annual" || raw === "yearly")
        return "annual";
    return DEFAULT_BILLING_CYCLE;
}
function getBillingCycleMonths(cycle) {
    switch (cycle) {
        case "quarterly":
            return 3;
        case "biannual":
            return 6;
        case "annual":
            return 12;
        default:
            return 1;
    }
}
/**
 * Advance a renewal boundary by one full commercial cycle from `start`
 * (same rules as the billing cron / tenant provisioning).
 */
function advanceCommercialCycleStart(start, cycle) {
    const end = new Date(start);
    switch (cycle) {
        case "quarterly":
            end.setMonth(end.getMonth() + 3);
            break;
        case "biannual":
            end.setMonth(end.getMonth() + 6);
            break;
        case "annual":
            end.setFullYear(end.getFullYear() + 1);
            break;
        default:
            end.setMonth(end.getMonth() + 1);
    }
    return end;
}
/**
 * First `nextRenewalAt` for a subscription: end of the initial billing period
 * after `startDate` (matches tenant provisioning and BillingCycleCron semantics).
 */
function computeInitialNextRenewalAt(startDate, cycle) {
    return advanceCommercialCycleStart(startDate, cycle);
}
function isUnlimitedPlanLimit(limit) {
    return typeof limit !== "number" || limit <= 0;
}
function planSupportsCounts(plan, counts) {
    const within = (limit, value) => isUnlimitedPlanLimit(limit) || value <= (limit ?? 0);
    return (within(plan.maxSeats, counts.seatCount) &&
        within(plan.maxVehicles, counts.vehicleCount) &&
        within(plan.maxDevices, counts.deviceCount));
}
function planSupportsBillingCycle(plan, cycle) {
    const supported = Array.isArray(plan.availableBillingCycles)
        ? plan.availableBillingCycles
        : [];
    if (supported.length === 0) {
        return cycle === DEFAULT_BILLING_CYCLE;
    }
    return supported.includes(cycle);
}
function readBillingCycleAdjustment(plan, cycle) {
    const rawAdjustments = plan.billingCycleAdjustments ?? {};
    const rawEntry = rawAdjustments[cycle];
    const entry = rawEntry && typeof rawEntry === "object" && !Array.isArray(rawEntry)
        ? rawEntry
        : {};
    return {
        discount: typeof entry.discount === "number" && Number.isFinite(entry.discount)
            ? Math.max(0, Math.min(95, entry.discount))
            : 0,
        minimumCommitment: typeof entry.minimumCommitment === "number" &&
            Number.isFinite(entry.minimumCommitment)
            ? Math.max(0, Math.floor(entry.minimumCommitment))
            : undefined,
        earlyTerminationFee: typeof entry.earlyTerminationFee === "number" &&
            Number.isFinite(entry.earlyTerminationFee)
            ? Math.max(0, entry.earlyTerminationFee)
            : undefined,
        setupFeeWaiver: entry.setupFeeWaiver === true,
    };
}
function normalizePackageValue(tenantPackage, industry) {
    const normalized = String(tenantPackage ?? "").trim().toLowerCase();
    if (normalized === "insurance")
        return "insurance";
    if (normalized === "farming" || normalized === "farmer")
        return "farming";
    return (0, tenant_access_1.resolveTenantPackage)(industry);
}
function buildServiceMap(plan, industry, tenantPackage) {
    const baseEnabledKeys = Object.entries(plan.servicesEnabled ?? {})
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
    const normalizedIndustry = (0, tenant_access_1.normalizeIndustrySelection)(industry);
    const resolvedPackage = normalizePackageValue(tenantPackage, normalizedIndustry);
    const bundleInputIndustry = normalizedIndustry ||
        (resolvedPackage === "insurance"
            ? "insurance"
            : resolvedPackage === "farming"
                ? "farming"
                : "");
    const bundled = (0, tenant_access_1.applyIndustryServiceBundle)(baseEnabledKeys, bundleInputIndustry);
    return Object.fromEntries(bundled.map((key) => [key, true]));
}
function planCapacityScore(plan) {
    const normalizeLimit = (value) => isUnlimitedPlanLimit(value) ? Number.MAX_SAFE_INTEGER / 4 : value;
    return (normalizeLimit(plan.maxSeats) +
        normalizeLimit(plan.maxVehicles) +
        normalizeLimit(plan.maxDevices));
}
function sortPlans(plans) {
    return [...plans].sort((left, right) => {
        const tierDelta = (TIER_PRIORITY[left.tierKey] ?? 999) - (TIER_PRIORITY[right.tierKey] ?? 999);
        if (tierDelta !== 0)
            return tierDelta;
        const capacityDelta = planCapacityScore(left) - planCapacityScore(right);
        if (capacityDelta !== 0)
            return capacityDelta;
        return left.id.localeCompare(right.id);
    });
}
function selectSmallestEligiblePlan(plans, counts) {
    for (const plan of sortPlans(plans)) {
        if (planSupportsCounts(plan, counts)) {
            return plan;
        }
    }
    return null;
}
function estimateCommercialCycleAmount(plan, cycle, counts) {
    const cycleMonths = getBillingCycleMonths(cycle);
    const adjustment = readBillingCycleAdjustment(plan, cycle);
    const recurringBaseMonthly = plan.monthlyFee +
        plan.perVehicleFee * counts.vehicleCount +
        plan.perDeviceFee * counts.deviceCount;
    const recurringBeforeDiscount = recurringBaseMonthly * cycleMonths;
    const recurringSubtotal = recurringBeforeDiscount * (1 - adjustment.discount / 100);
    const setupFee = adjustment.setupFeeWaiver ? 0 : plan.setupFee;
    return {
        cycleMonths,
        discountPercent: adjustment.discount,
        recurringSubtotal: roundMoney(recurringSubtotal),
        setupFee: roundMoney(setupFee),
        totalDue: roundMoney(recurringSubtotal + setupFee),
    };
}
function resolveCommercialSelection(input) {
    const activePlans = sortPlans((input.plans ?? []).filter((plan) => plan.status !== "archived"));
    if (activePlans.length === 0) {
        throw new Error("No active billing plans are available");
    }
    const counts = normalizeCommercialCounts({
        seatCount: input.seatCount,
        vehicleCount: input.vehicleCount,
        deviceCount: input.deviceCount,
    });
    const requestedCycle = normalizeCommercialBillingCycle(input.billingCycle);
    const normalizedIndustry = (0, tenant_access_1.normalizeIndustrySelection)(input.industry);
    const resolvedTenantPackage = normalizePackageValue(input.tenantPackage, normalizedIndustry);
    const requestedPlanId = input.requestedPlanId?.trim() || null;
    const warnings = [];
    const requestedPlan = requestedPlanId
        ? activePlans.find((plan) => plan.id === requestedPlanId) ?? null
        : null;
    const smallestEligible = selectSmallestEligiblePlan(activePlans, counts) ??
        activePlans[activePlans.length - 1];
    let selectedPlan = requestedPlan ?? smallestEligible;
    let autoSelected = false;
    if (!requestedPlan && selectedPlan.id !== smallestEligible.id) {
        selectedPlan = smallestEligible;
        autoSelected = true;
    }
    if (requestedPlan && !planSupportsCounts(requestedPlan, counts)) {
        selectedPlan = smallestEligible;
        autoSelected = true;
        warnings.push(`Requested plan ${requestedPlan.id} does not fit the selected seat, vehicle, and device counts. The closest eligible plan was selected automatically.`);
    }
    if (!requestedPlanId) {
        autoSelected = true;
    }
    let selectedCycle = requestedCycle;
    let cycleAdjusted = false;
    if (!planSupportsBillingCycle(selectedPlan, selectedCycle)) {
        selectedCycle =
            selectedPlan.availableBillingCycles?.[0] ?? DEFAULT_BILLING_CYCLE;
        cycleAdjusted = selectedCycle !== requestedCycle;
        warnings.push(`Billing cycle ${requestedCycle} is not available for plan ${selectedPlan.id}. ${selectedCycle} was selected instead.`);
    }
    return {
        requestedPlanId,
        requestedBillingCycle: requestedCycle,
        plan: selectedPlan,
        billingCycle: selectedCycle,
        counts,
        industry: normalizedIndustry,
        tenantPackage: resolvedTenantPackage,
        enabledServices: buildServiceMap(selectedPlan, normalizedIndustry, resolvedTenantPackage),
        autoSelected,
        cycleAdjusted,
        warnings,
    };
}
